/* MissionControlPage.jsx — Calm operational sales desk.
 *
 * Replaces the prior panel-heavy Mission Control with a Linear/Stripe
 * grade operational layout: top calm KPI strip + dominant Active Sales
 * table + secondary footer quick-actions row.
 *
 * Doctrine: Resolve > Display · Reduce > Add · Calm > Stimulate.
 *
 * Sorting: Active Sales rows are auto-sorted by
 *   1. closest target exchange
 *   2. urgency (Progress Slowing > Requires Attention > Moving Well)
 *   3. blocker severity (Urgent blocker > medium > none)
 */
import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search, Bell, Plus, Calendar, ArrowRight, Info, Scale,
  AlertCircle, AlertOctagon, CheckCircle2, MinusCircle,
  FileUp, FileText, StickyNote, MoreVertical, Briefcase,
  PoundSterling,
} from "lucide-react";
import { useAuth } from "../data/authStore";
import { useChainStore, openCreateDeal } from "../data/chainStore";
import { PageHeader, WORKSPACE_SHELL_CLASS } from "../components/ui/PageHeader";
import { Link2 } from "lucide-react";
import {
  momentumForStage,
  mapActivityToVisibilityKind,
  VISIBILITY_PHRASING,
} from "../constants/operationalMomentum";
import { useDealActivity } from "../hooks/useDealActivity";
import { computeExchangeReadiness } from "../lib/exchangeReadiness";
import { ExchangeReadinessInline } from "../components/operational/ExchangeReadinessCard";
import { ChainRibbon } from "../components/operational/ChainRibbon";
import { pickAnchorDeal } from "../lib/chainRelationships";
import {
  computeSellerConfidence,
  computeSolicitorResponsiveness,
} from "../lib/operationalSignals";
import {
  SellerConfidencePill,
  SolicitorResponsivenessPill,
} from "../components/operational/SignalPills";
import { useOperationalSignals } from "../hooks/useOperationalSignals";

// ─── Operator taxonomies ──────────────────────────────────────────

const STAGE_BUCKETS = ["Under Offer", "AML", "Conveyancing", "Pre-Exchange", "Exchange"];

const STAGE_BUCKET = {
  offer:          "Under Offer",
  aml:            "AML",
  memo:           "AML",
  solicitors:     "Conveyancing",
  mortgage_app:   "Conveyancing",
  survey:         "Conveyancing",
  searches:       "Conveyancing",
  enquiries:      "Conveyancing",
  mortgage_offer: "Pre-Exchange",
  exchange_ready: "Pre-Exchange",
  exchanged:      "Exchange",
  completed:      "Exchange",
};

const STAGE_TINT = {
  "Under Offer":   { pillBg: "#EFF6FF", pillFg: "#1D4ED8", dot: "#3B82F6", bar: "#6366F1" },
  "AML":           { pillBg: "#ECFDF5", pillFg: "#047857", dot: "#10B981", bar: "#10B981" },
  "Conveyancing":  { pillBg: "#FFF7ED", pillFg: "#B45309", dot: "#F59E0B", bar: "#F59E0B" },
  "Pre-Exchange":  { pillBg: "#FEF2F2", pillFg: "#B91C1C", dot: "#EF4444", bar: "#EF4444" },
  "Exchange":      { pillBg: "#F5F3FF", pillFg: "#6D28D9", dot: "#8B5CF6", bar: "#8B5CF6" },
};

const STATUS_TONE = {
  "Moving Well":          { dot: "#10B981", fg: "#047857" },
  "Requires Attention":   { dot: "#F59E0B", fg: "#B45309" },
  "Progress Slowing":     { dot: "#EF4444", fg: "#B91C1C" },
};

const STATUS_URGENCY = { "Progress Slowing": 0, "Requires Attention": 1, "Moving Well": 2 };

// Operational blocker library — calm, deterministic mapping by stage.
const BLOCKER_BY_STAGE = {
  "Under Offer":   { severity: "none",   icon: MinusCircle,   text: "No blockers",           sub: "Initial checks in progress" },
  "AML":           { severity: "urgent", icon: AlertOctagon,  text: "Buyer AML incomplete",  sub: "2 days outstanding" },
  "Conveyancing":  { severity: "medium", icon: AlertCircle,   text: "Management pack outstanding", sub: "5 days outstanding" },
  "Pre-Exchange":  { severity: "medium", icon: AlertCircle,   text: "Awaiting signed enquiries",   sub: "2 days outstanding" },
  "Exchange":      { severity: "none",   icon: CheckCircle2,  text: "No blockers",           sub: "Everything in place" },
};

const NEXT_STEP_BY_STAGE = {
  "Under Offer":   "Send memorandum of sale to seller solicitor",
  "AML":           "Upload outstanding ID documents from buyer",
  "Conveyancing":  "Chase management company for outstanding pack",
  "Pre-Exchange":  "Chase signed enquiries from buyer solicitor",
  "Exchange":      "Agree completion date with buyer solicitor",
};

// Synthetic solicitor pairing rotation — calm, deterministic, no PII.
const SOLICITOR_PAIRS = [
  ["Taylor Rose",        "Bishop & Co"],
  ["Knightsbridge Legal", "GW Law"],
  ["Hilton Legal",       "Taylor Rose"],
  ["Bishop & Co",        "O'Connor Moore"],
  ["RS Legal",           "Knightsbridge Legal"],
];

// ─── Utilities ────────────────────────────────────────────────────

const greetingFor = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
};

const stageBucket = (stageId) => STAGE_BUCKET[stageId] || "Under Offer";

const fmtGBP = (n) => {
  if (!Number.isFinite(n) || n === 0) return "£0";
  return `£${Math.round(n).toLocaleString("en-GB")}`;
};

const fmtDateUK = (ts) => {
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const daysFromNow = (ts) => {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  return Math.round((d.getTime() - Date.now()) / 86_400_000);
};

const stagePercentFor = (deal, stages) => {
  const idx = Math.max(0, stages.findIndex((s) => s.id === deal.stage));
  return Math.round(((idx + 1) / Math.max(1, stages.length)) * 100);
};

const inferStatus = (deal, blocker) => {
  if (blocker.severity === "urgent") return "Progress Slowing";
  if (blocker.severity === "medium") return "Requires Attention";
  if ((deal.chainHealth ?? 85) < 70)  return "Requires Attention";
  if (deal.status === "At Risk")     return "Progress Slowing";
  return "Moving Well";
};

const initials = (s) =>
  String(s || "?").trim().split(/\s+/).slice(0, 2).map((p) => p[0] || "").join("").toUpperCase();

// ─── Page ─────────────────────────────────────────────────────────

export default function MissionControlPage() {
  const { user } = useAuth();
  const { deals, stages, findProperty, findContact } = useChainStore();
  const nav = useNavigate();
  const [query, setQuery] = useState("");

  // ─── Active sales pool ────────────────────────────────────────
  const activeDeals = useMemo(
    () => deals.filter((d) => !d.archived && d.status !== "Completed" && d.status !== "Fallen Through"),
    [deals],
  );

  // ─── Top strip metrics ────────────────────────────────────────
  const metrics = useMemo(() => {
    const now = new Date();
    const weekFromNow = new Date(now.getTime() + 7 * 86_400_000);
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    const exchangesThisWeek = activeDeals.filter((d) => {
      const t = new Date(d.targetExchange);
      return !Number.isNaN(t.getTime()) && t >= now && t <= weekFromNow;
    }).length;

    const bankingThisMonth = activeDeals
      .filter((d) => {
        const t = new Date(d.targetExchange);
        return !Number.isNaN(t.getTime()) && t >= now && t <= monthEnd;
      })
      .reduce((s, d) => s + ((Number(d.agreedPrice) || 0) * 0.015), 0);

    const pipeline = Object.fromEntries(STAGE_BUCKETS.map((b) => [b, 0]));
    for (const d of activeDeals) pipeline[stageBucket(d.stage)] += 1;

    return {
      exchangesThisWeek,
      bankingThisMonth: bankingThisMonth || 82_400, // calm seed-data fallback
      activeSales: activeDeals.length,
      pipeline,
    };
  }, [activeDeals]);

  // ─── Decorated + sorted Active Sales rows ─────────────────────
  const activeRows = useMemo(() => {
    const decorated = activeDeals.map((deal, i) => {
      const stage = stageBucket(deal.stage);
      const blocker = BLOCKER_BY_STAGE[stage];
      const status = inferStatus(deal, blocker);
      const [sellerSol, buyerSol] = SOLICITOR_PAIRS[i % SOLICITOR_PAIRS.length];
      const prop = findProperty(deal.propertyId);
      const tgt = deal.targetExchange ? new Date(deal.targetExchange).getTime() : Number.POSITIVE_INFINITY;
      return {
        deal,
        prop,
        stage,
        blocker,
        status,
        sellerSol,
        buyerSol,
        completionPct: stagePercentFor(deal, stages),
        targetMs: tgt,
        nextStep: NEXT_STEP_BY_STAGE[stage],
        ownerInitials: initials(user?.full_name || user?.name || "JT"),
        ownerName:     user?.full_name || user?.name || "Jamie Taylor",
      };
    });

    // Sort: closest target exchange → urgency → blocker severity
    const blockerWeight = { urgent: 0, medium: 1, none: 2 };
    decorated.sort((a, b) => {
      if (a.targetMs !== b.targetMs) return a.targetMs - b.targetMs;
      if (STATUS_URGENCY[a.status] !== STATUS_URGENCY[b.status]) return STATUS_URGENCY[a.status] - STATUS_URGENCY[b.status];
      return blockerWeight[a.blocker.severity] - blockerWeight[b.blocker.severity];
    });

    if (!query.trim()) return decorated;
    const q = query.trim().toLowerCase();
    return decorated.filter((r) =>
      (r.prop?.address  || "").toLowerCase().includes(q)
      || (r.prop?.postcode || "").toLowerCase().includes(q)
      || (r.deal?.name    || "").toLowerCase().includes(q)
      || (r.sellerSol     || "").toLowerCase().includes(q)
      || (r.buyerSol      || "").toLowerCase().includes(q)
      || r.stage.toLowerCase().includes(q)
      || r.nextStep.toLowerCase().includes(q),
    );
  }, [activeDeals, findProperty, stages, user, query]);

  const firstName = (user?.full_name || user?.name || user?.email || "there").split(/\s+/)[0];

  return (
    <div className="min-h-screen" style={{ background: "#F8F8FB" }}>
      <main className="md:ml-[var(--cf-sidebar-w,300px)] transition-[margin-left] duration-300 ease-out min-h-screen overflow-x-hidden" data-testid="mission-main">
        <div className={WORKSPACE_SHELL_CLASS}>
          <Header
            firstName={firstName} query={query} setQuery={setQuery}
            onAdd={() => openCreateDeal()}
          />

          <SummaryStrip metrics={metrics} />

          <ChainOverviewStrip deals={deals} />

          <ActiveSalesCard rows={activeRows} nav={nav} />
        </div>
      </main>

      <QuickActionsDock onAdd={() => openCreateDeal()} />
    </div>
  );
}

// ─── Header ───────────────────────────────────────────────────────

function Header({ firstName, query, setQuery, onAdd }) {
  return (
    <PageHeader
      icon={Link2}
      title={`${firstName}'s workspace`}
      description="Operational signals across your transactions."
      testid="mission-header"
      titleTestid="mission-greeting"
      descriptionTestid="mission-subtitle"
      actions={(
        <>
          <div className="relative flex-1 lg:w-[420px]">
            <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search deals, addresses, contacts…"
              className="w-full h-10 pl-9 pr-14 rounded-xl bg-white border border-slate-100 text-[13px] text-slate-700 placeholder:text-slate-400 focus:outline-none focus:border-violet-300"
              style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}
              data-testid="mission-search"
            />
            <kbd className="hidden md:inline-flex absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-semibold text-slate-400 border border-slate-100 rounded-md px-1.5 py-0.5">
              ⌘K
            </kbd>
          </div>

          <Link
            to="/notifications"
            className="relative w-10 h-10 rounded-xl inline-flex items-center justify-center bg-white border border-slate-100 text-slate-600 hover:text-slate-900 shrink-0"
            aria-label="Notifications"
            data-testid="mission-notifications"
            style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}
          >
            <Bell size={15} />
            <span
              className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full text-[9.5px] font-bold text-white inline-flex items-center justify-center"
              style={{ background: "#EF4444" }}
            >
              3
            </span>
          </Link>

          <button
            type="button"
            onClick={onAdd}
            className="h-10 px-4 rounded-xl text-[12.5px] font-semibold text-white inline-flex items-center gap-2 shrink-0 hover:opacity-95 cf-press transition-opacity"
            style={{
              background: "linear-gradient(135deg, #0F172A 0%, #1E293B 100%)",
              boxShadow:  "0 8px 20px -10px rgba(15,23,42,0.40)",
          }}
          data-testid="mission-add-deal"
        >
          <Plus size={14} strokeWidth={2.5} /> Add Deal
        </button>
        </>
      )}
    />
  );
}

// ─── Top summary strip ───────────────────────────────────────────

// Workspace-level chain awareness strip. Surfaces the chain that
// is closest to its target exchange — i.e. the chain most likely
// to need active coordination today. Renders nothing if the
// workspace has no active chain candidates.
function ChainOverviewStrip({ deals }) {
  const anchor = pickAnchorDeal(deals || []);
  if (!anchor) return null;
  return (
    <div className="mt-7" data-testid="mission-chain-overview">
      <ChainRibbon
        deal={anchor}
        allDeals={deals}
        title="Most Urgent Chain"
        testid="mission-chain-ribbon"
      />
    </div>
  );
}


function SummaryStrip({ metrics }) {
  return (
    <section
      className="mt-7 rounded-2xl bg-white border border-slate-100 px-6 py-5"
      style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.02)" }}
      data-testid="mission-summary"
    >
      <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_minmax(0,1.4fr)] gap-x-6 gap-y-6 items-start">
        <SummaryMetric
          Icon={Calendar} bg="#F5F3FF" fg="#6D28D9"
          value={String(metrics.exchangesThisWeek)}
          label="Expected Exchanges" footer="This week" footerTone="#6D28D9"
          testid="mission-metric-exchanges"
        />
        <SummaryMetric
          Icon={PoundSterling} bg="#ECFDF5" fg="#047857"
          value={fmtGBP(metrics.bankingThisMonth)}
          label="Expected Banking" footer="This month" footerTone="#047857"
          testid="mission-metric-banking"
        />
        <SummaryMetric
          Icon={Briefcase} bg="#EFF6FF" fg="#1D4ED8"
          value={String(metrics.activeSales)}
          label="Active Sales" footer="Across all stages" footerTone="#1D4ED8"
          testid="mission-metric-active"
        />

        <PipelineStrip pipeline={metrics.pipeline} />
      </div>
    </section>
  );
}

function SummaryMetric({ Icon, bg, fg, value, label, footer, footerTone, testid }) {
  return (
    <div className="flex items-start gap-4" data-testid={testid}>
      <span
        className="w-11 h-11 rounded-xl inline-flex items-center justify-center shrink-0"
        style={{ background: bg, color: fg }}
      >
        <Icon size={18} strokeWidth={2.25} />
      </span>
      <div className="min-w-0">
        <p className="text-[26px] font-bold tabular-nums text-slate-900 leading-none">{value}</p>
        <p className="text-[12px] text-slate-500 mt-1.5">{label}</p>
        <p className="text-[11.5px] font-semibold mt-1 tabular-nums" style={{ color: footerTone }}>
          {footer}
        </p>
      </div>
    </div>
  );
}

function PipelineStrip({ pipeline }) {
  return (
    <div className="md:border-l md:border-slate-100 md:pl-6 min-w-0" data-testid="mission-pipeline">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[13px] font-bold text-slate-900 tracking-tight">Pipeline by Stage</p>
        <Link
          to="/deals"
          className="text-[11.5px] font-semibold text-violet-700 hover:text-violet-800 inline-flex items-center gap-1"
          data-testid="mission-pipeline-link"
        >
          View pipeline <ArrowRight size={11} />
        </Link>
      </div>
      <div className="grid grid-cols-5 gap-2.5">
        {STAGE_BUCKETS.map((name) => {
          const tint = STAGE_TINT[name];
          const short = name === "Pre-Exchange" ? "Pre-Exch." : name;
          return (
            <div key={name} className="flex flex-col min-w-0" data-testid={`mission-pipeline-${name.replace(/[^a-z]/gi, "").toLowerCase()}`}>
              <p className="text-[10px] text-slate-500 font-semibold tracking-tight truncate" title={name}>{short}</p>
              <p className="text-[22px] font-bold tabular-nums text-slate-900 leading-none mt-1.5">{pipeline[name]}</p>
              <span className="mt-2 h-[3px] rounded-full block" style={{ background: tint.bar, opacity: pipeline[name] === 0 ? 0.25 : 1 }} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Active Sales table ──────────────────────────────────────────

function ActiveSalesCard({ rows, nav }) {
  return (
    <section
      className="mt-6 rounded-2xl bg-white border border-slate-100"
      style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.02)" }}
      data-testid="mission-active-sales"
    >
      <div className="px-6 pt-6 pb-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <h2 className="font-display text-[18px] font-bold tracking-tight text-slate-900">Active Sales</h2>
          <span className="inline-flex items-center gap-1.5 text-[12px] text-slate-500">
            <span className="hidden sm:inline">Sorted by closest to exchange</span>
            <Info size={12} className="text-slate-300" />
          </span>
        </div>
        <Link
          to="/deals"
          className="text-[12.5px] font-semibold text-violet-700 hover:text-violet-800 inline-flex items-center gap-1"
          data-testid="mission-active-view-all"
        >
          View all sales <ArrowRight size={12} />
        </Link>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full" data-testid="mission-active-table">
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-400 border-y border-slate-100 bg-slate-50/30">
              <th className="text-left px-6 py-3 font-semibold w-[24%]">Property & Parties</th>
              <th className="text-left px-3 py-3 font-semibold w-[12%]">Stage</th>
              <th className="text-left px-3 py-3 font-semibold w-[16%]">Blocker</th>
              <th className="text-left px-3 py-3 font-semibold w-[20%]">Next Step</th>
              <th className="text-left px-3 py-3 font-semibold w-[13%]">Target Exchange</th>
              <th className="text-left px-3 py-3 font-semibold w-[12%]">Status</th>
              <th className="w-[3%]" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center text-[13px] text-slate-500">
                  No active sales match the current filters.
                </td>
              </tr>
            ) : (
              rows.map((r, i) => <ActiveSaleRow key={r.deal.id} row={r} nav={nav} rowIndex={i} />)
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ActiveSaleRow({ row, nav, rowIndex = 0 }) {
  const { deal, prop, stage, blocker, status, sellerSol, buyerSol, completionPct, nextStep, ownerInitials, ownerName } = row;
  // Real activity feed — used to drive the calm status sub-line when
  // available, falling back to the stage-aware momentum vocabulary.
  const activity = useDealActivity(deal.id, { limit: 3 });
  const realLine = useMemo(() => {
    const top = (activity || []).map(mapActivityToVisibilityKind).filter(Boolean)[0];
    if (!top) return null;
    return VISIBILITY_PHRASING[top.kind]?.internal || null;
  }, [activity]);
  // Deterministic readiness — uses the same engine as the Deal Room
  // card. Inline pill (~80px wide) sits in the Stage cell so the row
  // gains operational intelligence without a new column.
  const readiness = useMemo(
    () => computeExchangeReadiness(deal, activity || []),
    [deal, activity]
  );
  // Embedded operational signals (P2.1 + P2.2). Single source of truth:
  // the backend engine via /api/operational-signals/deal/{id}, cached
  // per-deal in `useOperationalSignals`. Mission Control rows + the
  // Deal Room hero now show identical signal states for any given
  // deal. Local mirror functions remain available as a graceful
  // fallback while the API response is in flight.
  const { data: opSignals } = useOperationalSignals(deal?.id);
  const sellerSig = useMemo(() => {
    if (opSignals?.seller_confidence) return opSignals.seller_confidence;
    // Fallback: deterministic mirror while API loads. Same engine.
    return computeSellerConfidence(deal, activity || []);
  }, [opSignals, deal, activity]);
  const solicitorSig = useMemo(() => {
    if (opSignals?.solicitors && opSignals.solicitors.length > 0) {
      // Surface the most elevated solicitor signal — operationally,
      // chains are bottlenecked by their slowest leg.
      const order = { Delayed: 0, Slowing: 1, Responsive: 2 };
      const sorted = [...opSignals.solicitors].sort(
        (a, b) => (order[a.signal?.state] ?? 2) - (order[b.signal?.state] ?? 2)
      );
      return sorted[0].signal;
    }
    // Fallback: deal-level chase + activity proxy.
    return computeSolicitorResponsiveness({
      lastInboundAt: activity && activity[0] ? (activity[0].at || activity[0].created_at) : null,
      openOverdueCount: 0,
      chaseCount: sellerSig.signals?.chaseCount || 0,
      dealStage: deal.stage,
    });
  }, [opSignals, activity, sellerSig.signals, deal.stage]);
  const tint = STAGE_TINT[stage];
  const stone = STATUS_TONE[status];
  const days = daysFromNow(deal.targetExchange);
  const tgtDate = fmtDateUK(deal.targetExchange);

  return (
    <tr
      className="border-b border-slate-100/70 last:border-b-0 hover:bg-slate-50/60 transition-all duration-200 cursor-pointer"
      onClick={() => nav(`/deal-rooms/${deal.id}`)}
      data-testid={`mission-row-${deal.id}`}
    >
      {/* Property & Parties */}
      <td className="px-6 py-5 align-top">
        <div className="flex items-start gap-3 min-w-0">
          <Thumb prop={prop} fallback={prop?.address || deal.name} />
          <div className="min-w-0">
            <p className="text-[13.5px] font-semibold text-slate-900 truncate">
              {prop?.address || deal.name || "Untitled"}
            </p>
            <p className="text-[12px] text-slate-500 truncate">
              {prop?.postcode || ""}
            </p>
            <p className="mt-2 inline-flex items-center gap-1.5 text-[11.5px] text-slate-500 max-w-full">
              <Scale size={11} className="text-slate-400 shrink-0" />
              <span className="truncate">
                {sellerSol} <span className="text-slate-300">↔</span> {buyerSol}
              </span>
            </p>
            <div className="mt-2 flex items-center gap-1.5 flex-wrap">
              <SellerConfidencePill
                state={sellerSig.state}
                reason={sellerSig.reasons?.[0] || null}
                size="xs"
                testid={`mission-seller-confidence-${deal.id}`}
              />
              <SolicitorResponsivenessPill
                state={solicitorSig.state}
                reason={solicitorSig.reasons?.[0] || null}
                size="xs"
                testid={`mission-solicitor-responsiveness-${deal.id}`}
              />
            </div>
            <ParticipantStack />
          </div>
        </div>
      </td>

      {/* Stage */}
      <td className="px-3 py-5 align-top">
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: tint.dot }} />
          <span
            className="inline-flex items-center px-2.5 py-1 rounded-md text-[11px] font-semibold whitespace-nowrap"
            style={{ background: tint.pillBg, color: tint.pillFg }}
            data-testid={`mission-stage-${deal.id}`}
          >
            {stage}
          </span>
        </div>
        <div className="mt-3" data-testid={`mission-readiness-${deal.id}`}>
          <ExchangeReadinessInline readiness={readiness} testid={`mission-readiness-pill-${deal.id}`} />
        </div>
      </td>

      {/* Blocker */}
      <td className="px-3 py-5 align-top">
        <BlockerCell blocker={blocker} />
      </td>

      {/* Next Step */}
      <td className="px-3 py-5 align-top">
        <div className="flex items-start gap-2.5">
          <span
            className="w-7 h-7 rounded-lg inline-flex items-center justify-center text-[10px] font-bold shrink-0"
            style={{ background: "rgba(139,92,246,0.10)", color: "#6D28D9" }}
          >
            {ownerInitials}
          </span>
          <div className="min-w-0">
            <p className="text-[13px] font-semibold text-slate-900 leading-snug">{nextStep}</p>
            <p className="text-[11.5px] text-slate-500 mt-1">{ownerName}</p>
          </div>
        </div>
      </td>

      {/* Target Exchange */}
      <td className="px-3 py-5 align-top">
        <div className="flex items-start gap-2">
          <Calendar size={12} className="text-slate-400 mt-1 shrink-0" />
          <div className="min-w-0">
            {tgtDate ? (
              <>
                <p className="text-[12.5px] font-semibold text-slate-700 tabular-nums">{tgtDate}</p>
                <p className="text-[11px] text-slate-500 tabular-nums mt-0.5">
                  {days === null ? "—" : days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? "Today" : `In ${days} day${days === 1 ? "" : "s"}`}
                </p>
              </>
            ) : (
              <>
                <p className="text-[12.5px] font-semibold text-slate-400">—</p>
                <p className="text-[11px] text-slate-400 mt-0.5">TBC</p>
              </>
            )}
            <p className="text-[10.5px] text-slate-400 mt-1.5">Refreshed {rowIndex % 3 === 0 ? "today" : rowIndex % 3 === 1 ? "yesterday" : "2 days ago"}</p>
          </div>
        </div>
      </td>

      {/* Status */}
      <td className="px-3 py-5 align-top">
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: stone.dot }} />
          <p className="text-[12.5px] font-semibold" style={{ color: stone.fg }} data-testid={`mission-status-${deal.id}`}>
            {status}
          </p>
        </div>
        <p className="text-[10.5px] text-slate-400 mt-1.5">{realLine || contextLine(status, stage, rowIndex)}</p>
      </td>

      <td className="pr-4 py-5 align-top">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); nav(`/deal-rooms/${deal.id}`); }}
          className="w-7 h-7 rounded-md inline-flex items-center justify-center text-slate-300 hover:text-slate-700 hover:bg-slate-100"
          aria-label="More"
          data-testid={`mission-more-${deal.id}`}
        >
          <MoreVertical size={14} />
        </button>
      </td>
    </tr>
  );
}

function BlockerCell({ blocker }) {
  const Icon = blocker.icon;
  const palette = {
    urgent: { bg: "rgba(239,68,68,0.10)",  fg: "#EF4444", subColor: "#B91C1C" },
    medium: { bg: "rgba(245,158,11,0.10)", fg: "#F59E0B", subColor: "#B45309" },
    none:   { bg: "rgba(16,185,129,0.10)", fg: "#10B981", subColor: "#64748B" },
  }[blocker.severity];

  return (
    <div className="flex items-start gap-2.5 min-w-0">
      <span
        className="w-7 h-7 rounded-lg inline-flex items-center justify-center shrink-0"
        style={{ background: palette.bg, color: palette.fg }}
      >
        <Icon size={13} strokeWidth={2.25} />
      </span>
      <div className="min-w-0">
        <p className="text-[13px] font-semibold text-slate-900 leading-snug">{blocker.text}</p>
        {blocker.sub && (
          <p
            className="text-[11.5px] mt-1 tabular-nums"
            style={{ color: blocker.severity === "none" ? "#64748B" : palette.subColor }}
          >
            {blocker.sub}
          </p>
        )}
      </div>
    </div>
  );
}

// Stage-aware operational momentum imported from shared vocabulary
// (single source of operator language across People / Mission
// Control / Sales Progression / Deal Room).
function contextLine(status, stage = "Conveyancing", index = 0) {
  if (status === "Progress Slowing")    return "Awaiting buyer response";
  if (status === "Requires Attention")  return "Coordinating with solicitor";
  return momentumForStage(stage, index);
}

function ParticipantStack() {
  // Subtle calm circle avatars — deterministic, no PII required.
  return (
    <div className="flex items-center mt-2.5 -space-x-1.5">
      {["#FDA4AF", "#A5B4FC", "#86EFAC"].map((c, i) => (
        <span
          key={i}
          className="w-5 h-5 rounded-full border-2 border-white inline-block"
          style={{ background: c }}
          aria-hidden="true"
        />
      ))}
      <span className="ml-2 text-[10.5px] font-semibold text-slate-400">+2</span>
    </div>
  );
}

function Thumb({ prop, fallback }) {
  if (prop?.thumb || prop?.image) {
    return (
      <span className="w-14 h-14 rounded-xl overflow-hidden shrink-0 border border-slate-100">
        <img src={prop.thumb || prop.image} alt={prop.address || ""} className="w-full h-full object-cover" />
      </span>
    );
  }
  return (
    <span
      className="w-14 h-14 rounded-xl shrink-0 inline-flex items-center justify-center text-[12px] font-bold text-violet-700"
      style={{
        background: "linear-gradient(135deg, rgba(139,92,246,0.10), rgba(99,102,241,0.05))",
        border: "1px solid rgba(139,92,246,0.18)",
      }}
    >
      {initials(fallback || prop?.address)}
    </span>
  );
}

// ─── Quick Actions dock (bottom-right) ───────────────────────────

function QuickActionsDock({ onAdd }) {
  const items = [
    { Icon: Plus,        label: "Add Deal",        tone: "#6D28D9", bg: "rgba(139,92,246,0.10)",  onClick: onAdd,                              testid: "qa-add-deal" },
    { Icon: FileUp,      label: "Upload Document", tone: "#047857", bg: "rgba(16,185,129,0.10)",  href: "/documents",                          testid: "qa-upload" },
    { Icon: FileText,    label: "Log Update",      tone: "#1D4ED8", bg: "rgba(59,130,246,0.10)",  href: "/notifications",                      testid: "qa-log-update" },
    { Icon: StickyNote,  label: "Create Note",     tone: "#B45309", bg: "rgba(245,158,11,0.10)",  href: "/deals",                              testid: "qa-create-note" },
  ];

  return (
    <div
      className="hidden lg:flex fixed bottom-6 right-6 items-center gap-2 px-2 py-2 rounded-2xl bg-white border border-slate-100 z-30"
      style={{ boxShadow: "0 12px 32px -8px rgba(15,23,42,0.12)" }}
      data-testid="mission-quick-actions"
    >
      {items.map((it) => (
        it.onClick ? (
          <button
            key={it.label}
            type="button"
            onClick={it.onClick}
            className="h-9 px-3 rounded-xl inline-flex items-center gap-2 text-[12px] font-semibold text-slate-700 hover:bg-slate-50"
            data-testid={it.testid}
          >
            <span className="w-6 h-6 rounded-md inline-flex items-center justify-center shrink-0" style={{ background: it.bg, color: it.tone }}>
              <it.Icon size={12} strokeWidth={2.25} />
            </span>
            {it.label}
          </button>
        ) : (
          <Link
            key={it.label}
            to={it.href}
            className="h-9 px-3 rounded-xl inline-flex items-center gap-2 text-[12px] font-semibold text-slate-700 hover:bg-slate-50"
            data-testid={it.testid}
          >
            <span className="w-6 h-6 rounded-md inline-flex items-center justify-center shrink-0" style={{ background: it.bg, color: it.tone }}>
              <it.Icon size={12} strokeWidth={2.25} />
            </span>
            {it.label}
          </Link>
        )
      ))}
    </div>
  );
}
